import type { Rect } from '@flobi/artwork-core';
import { hexColor } from '../colors';
import { PenTool } from '../penTool';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * What a press of the pen means.
 *
 * Illustrator keeps these four behind a flyout on the pen and then you have to know they
 * are there. They are the rail's top well instead, which is where a tool says how it
 * works — and it holds exactly four, which is exactly how many there are.
 */
type PenMode = 'draw' | 'add' | 'remove' | 'convert';

/** A touch screen, where the pen's keys need buttons. A pen or a mouse has a keyboard beside it. */
const COARSE = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

const MODES: { id: PenMode; icon: IconName; title: string; panelGroup: string }[] = [
  { id: 'draw', icon: 'pen', panelGroup: 'Create', title: 'Draw path — Click to add points. Drag to curve the path.' },
  { id: 'add', icon: 'plus', panelGroup: 'Edit points', title: 'Add point — Click a path edge to insert a point.' },
  { id: 'remove', icon: 'minus', panelGroup: 'Edit points', title: 'Remove point — Click an existing point to remove it.' },
  { id: 'convert', icon: 'smooth', panelGroup: 'Edit points', title: 'Convert point — Click a point to switch between a corner and a smooth curve.' },
];

/**
 * The pen, as a tool.
 *
 * The drawing itself lives in PenTool, which knows nothing about pointers: this wires
 * a press, a drag and a release to it, and hands its preview to the overlay.
 */
export function penTool(origin: unknown): Tool {
  let pen: PenTool | null = null;
  let pressed = false;
  /** Which of the pen's four a press means. Kept across tool changes, as a mode should be. */
  let mode: PenMode = 'draw';

  /**
   * The path under the pointer, and the anchor of it within reach.
   *
   * Working on a path with the pen means working on whatever is under the hand, without
   * choosing it first — the same bargain the point cursors make, for the same reason.
   */
  const under = (ctx: ToolContext, world: Point): { id: string; index: number | null } | null => {
    const reach = 8 * ctx.unitsPerPixel;
    const around: Rect = { x: world.x - reach, y: world.y - reach, width: 2 * reach, height: 2 * reach };
    // What a hand could reach — not hidden, not in a locked group — which is also all the
    // points editor will open (select-points-F19).
    const paths = ctx.nearby(around).filter((id) => ctx.node(id)?.type === 'path' && ctx.scene.choosable(id));
    const anchor = ctx.points.anchorNear(world, paths);
    if (anchor) return { id: anchor.id, index: anchor.index };
    // On the outline rather than merely over the shape: adding a point happens on the
    // line, so being inside a filled path is not being anywhere a point can go.
    for (const id of paths) if (ctx.points.segmentNear(world, id)) return { id, index: null };
    return null;
  };

  /** Opens the path a press landed on, so the editing methods have something to work on. */
  const open = (ctx: ToolContext, id: string): boolean => {
    if (ctx.points.editingId === id) return true;
    ctx.setSelection([id]);
    return ctx.points.open(id);
  };

  const make = (ctx: ToolContext) =>
    new PenTool({
      page: ctx.page,
      scene: ctx.scene,
      origin,
      style: () => ctx.style,
      color: hexColor,
      // The pen measures closeness in screen pixels; the context knows the scale.
      toScreen: (point) => ({ x: point.x / ctx.unitsPerPixel, y: point.y / ctx.unitsPerPixel }),
      // The same answer the shape tools get: the topmost artboard that can be seen
      // (QA 2026-09-25, pen-F13).
      artboardAt: (world) => ctx.artboardAt(world),
      onChange: () => ctx.refresh(),
      onFinish: (id) => {
        ctx.setSelection(id ? [id] : []);
        ctx.finish();
      },
      closeStep: () => ctx.stopCapturing(),
    });

  return {
    id: 'pen',

    activate(ctx) {
      pen = make(ctx);
    },

    holdsStep: () => pen?.active ?? false,

    deactivate(ctx) {
      pen?.keep();
      pen = null;
      pressed = false;
      ctx.setCursor(null);
    },

    onPointerDown(_event, world, ctx) {
      if (mode !== 'draw') {
        const near = under(ctx, world);
        // A path the points editor will not open is left alone, rather than the edit
        // landing on whichever path was open before, by this one's index.
        if (!near || !open(ctx, near.id)) return true;
        if (mode === 'add') {
          // Where the hand is, not the middle of the piece: the pen's add tool puts a
          // point under the pointer, which is the whole of what it is for. On an anchor
          // already there it only opens the path — every such click used to stack another
          // invisible point on top (QA 2026-09-25, pen-F11).
          const segment = near.index === null ? ctx.points.segmentNear(world, near.id) : null;
          if (segment) ctx.points.addPointAt(segment, 'here');
        } else if (near.index !== null) {
          if (mode === 'remove') ctx.points.removeAt(near.index);
          else ctx.points.convertAt(near.index);
        }
        ctx.stopCapturing();
        ctx.railChanged();
        ctx.refresh();
        return true;
      }

      pen ??= make(ctx);
      // The second press of a double click is the double click's, which ends the path: it
      // used to add the last point again on top of itself (QA 2026-09-25, pen-F8).
      if (pen.active && ctx.clicks >= 2) return true;
      if (ctx.text?.current) ctx.text.stop();
      // The path being drawn is not something to line ITSELF up with: its own last
      // anchor is always the nearest mark there is, and every point after the first
      // would land on top of the one before it.
      const started = pen.active;
      pen.press(ctx.place(world, pen.pathId ? [pen.pathId] : []));
      pressed = pen.active;
      // A path begun brings the touch strip's Back a point and Done (rail, below).
      if (!started && pen.active && COARSE()) ctx.railChanged();
      return true;
    },

    onPointerMove(event, world, ctx) {
      if (mode !== 'draw') {
        if (event.buttons === 0) ctx.setCursor(under(ctx, world) ? 'copy' : null);
        return;
      }
      if (!pen) return;
      // With nothing pressed the line follows the pointer; with the button down the
      // drag is pulling a curve out of the anchor just placed.
      if (event.buttons === 0) {
        // The ghost has to land where the NEXT press will, or it is a preview of
        // something that will not happen.
        pen.move(ctx.place(world, pen.pathId ? [pen.pathId] : []));
        // The pointer says what the next click does: closing is the one press in drawing
        // a shape that ends it, and it should not be a surprise.
        ctx.setCursor(pen.preview()?.closing ? 'pointer' : null);
      } else if (pressed) pen.drag(world);
    },

    onPointerUp() {
      pen?.release();
      pressed = false;
    },

    onDoubleClick(_event, _world) {
      if (!pen?.active) return false;
      pen.finish(false);
      return true;
    },

    onKeyDown(event) {
      return pen?.handleKey(event) ?? false;
    },

    /**
     * The pen's four ways of working above, and how heavy the line is below.
     *
     * Drawing a path and changing one are the same job done at different moments, so they
     * are one tool with four modes rather than four tools — which is also why the modes
     * sit where every other tool says how it works.
     */
    rail(ctx) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        panelGroup: item.panelGroup,
        pressed: mode === item.id,
        run: () => {
          // The mode already in hand is not a request to do anything (pen-F12).
          if (item.id === mode) return;
          // A path half drawn is kept as it stands and the pen stays in hand: finishing it
          // handed the canvas back to Select, so the mode just chosen never arrived
          // (QA 2026-09-25, pen-F12).
          if (mode === 'draw' && pen?.active) {
            const drawn = pen.pathId;
            pen.keep();
            if (drawn && ctx.node(drawn)) ctx.setSelection([drawn]);
          }
          mode = item.id;
          // Leaving the editing modes closes the path: its anchors would be showing with
          // nothing able to reach them.
          if (mode === 'draw') ctx.points.close();
          ctx.railChanged();
          ctx.refresh();
        },
      }));
      /**
       * Mid-path, a finger has no Return, Escape or Backspace (the responsive pass, 2026-10-26): on a
       * touch screen the strip offers them as buttons, beside the double-tap that also finishes.
       */
      const keys: RailButton[] = mode === 'draw' && pen?.active && COARSE()
        ? [
            { id: 'pen-back', icon: 'undo', title: 'Take the last point back', label: 'Back a point', run: () => { pen?.back(); ctx.railChanged(); ctx.refresh(); } },
            { id: 'pen-done', icon: 'check', title: 'Finish the path', label: 'Done', run: () => pen?.finish(false) },
          ]
        : [];
      return { top: { kind: 'buttons', items }, ...(keys.length ? { bottom: { kind: 'buttons' as const, items: keys } } : {}), style: mode === 'draw' };
    },

    draw(overlay, ctx) {
      // In the editing modes the pen shows the path it would work on and every anchor of
      // it: three of the four are about a point, and a point you cannot see is a point you
      // cannot aim at.
      if (mode !== 'draw') {
        const editing = ctx.points.editingId;
        if (editing) {
          for (const outline of ctx.points.outlineOf(editing) ?? []) overlay.curve(outline.map((at) => ({ at })), { width: 1 });
          for (const anchor of ctx.points.anchorsOf(editing) ?? []) overlay.handle(anchor.at);
        }
        return;
      }

      const drawing = pen?.preview();
      if (!drawing) return;
      const world = ctx.scene.worldTransform(drawing.id);
      const place = (x: number, y: number) => ({
        x: world[0]! * x + world[2]! * y + world[4]!,
        y: world[1]! * x + world[3]! * y + world[5]!,
      });

      // The path so far is drawn here rather than left to its own stroke: a hairline at
      // 30% zoom is a third of a pixel, and a path you cannot see while you draw it is
      // no use.
      overlay.curve(
        (drawing.closingDrag ? [...drawing.points, drawing.points[0]!] : drawing.points).map((point) => ({
          at: place(point.x, point.y),
          ...(point.in ? { in: place(point.in[0], point.in[1]) } : {}),
          ...(point.out ? { out: place(point.out[0], point.out[1]) } : {}),
        })),
        { width: 1.5 },
      );

      const last = drawing.points[drawing.points.length - 1];
      const first = drawing.points[0];
      if (last && drawing.cursor) {
        // Snapped to the first anchor while a click would close the path, because that
        // is where the click lands. Left at the pointer it draws a shape with a gap in
        // it right up until the moment it does not, and you have to take it on faith
        // that the ends will meet.
        const to = drawing.closing && first ? place(first.x, first.y) : drawing.cursor;
        overlay.line(place(last.x, last.y), to, { width: 1 });
      }
      const activePoint = drawing.closingDrag ? first : last;
      if (activePoint) {
        const anchor = place(activePoint.x, activePoint.y);
        for (const handle of [activePoint.out, activePoint.in]) {
          if (!handle) continue;
          const at = place(handle[0], handle[1]);
          overlay.line(anchor, at, { width: 1 });
          overlay.knob(at);
        }
      }
      drawing.points.forEach((point, index) => {
        // The first anchor grows when the pointer is near it: that click closes the path.
        overlay.handle(place(point.x, point.y), { filled: true, hovered: index === 0 && drawing.closing });
      });
    },
  };
}
